/**
 * PageShell.jsx
 *
 * Shared page layout wrapper for all internal (non-Home) authenticated pages.
 * Provides visual consistency with the Home page by rendering:
 * - A gradient background matching Home
 * - A branded page header bar (back button → Home, title with optional icon,
 *   notification bell, storage gauge, user avatar)
 * - Mobile: 3 header-right elements consolidated into single avatar dropdown
 * - Desktop/Tablet: 3 separate elements (notification, storage, avatar)
 * - A centered content container with responsive padding and nav-rail clearance
 *
 * Props:
 * @param {string}  title          - Page title displayed in the header
 * @param {string}  [titleIcon]    - Optional icon image URL shown inline next to title (32px)
 * @param {boolean} [showHeader=true]  - Whether to render the page header bar
 * @param {boolean} [showBackButton=true] - Whether to show the back-to-Home button
 * @param {boolean} [padContent=true]   - Whether to apply dash-shell padding to content
 * @param {number}  [storageUsedGb=2.3]  - Storage used in GB (for gauge)
 * @param {number}  [storageTotalGb=15]  - Total storage in GB (for gauge)
 * @param {number}  [unreadCount=3]      - Unread notification count
 * @param {React.ReactNode} children - Page content to render inside the shell
 *
 * @component
 * @example
 * <PageShell title="Dental Records" titleIcon={medicalRecordsIcon}>
 *   <MedicalRecordsList />
 * </PageShell>
 */
import {useState, useRef, useEffect} from 'react';
import {useNavigate, Link} from 'react-router';
import {useAuth} from '../hooks/AuthContext';
import * as AccountApi from '../api/AccountApi';
import './PageShell.css';

export default function PageShell({
  title = '',
  titleIcon = null,
  showHeader = true,
  showBackButton = true,
  padContent = true,
  storageUsedGb = 2.3,
  storageTotalGb = 15,
  unreadCount = 3,
  children,
}) {
  const navigate = useNavigate();
  const auth = useAuth();

  /* ------------------------------------------------------------------
   * Static IDs for ARIA attributes (storage popup + mobile menu)
   * ------------------------------------------------------------------ */
  const storagePopId = 'ps-storage-pop';
  const mobileMenuId = 'ps-mobile-menu';

  const [dynamicStorage, setDynamicStorage] = useState({
    used: storageUsedGb * 1024 * 1024 * 1024,
    total: storageTotalGb * 1024 * 1024 * 1024,
  });

  useEffect(() => {
    AccountApi.getStorageUsage()
      .then((data) => {
        if (data && data.total_storage) {
          setDynamicStorage({used: data.used_storage, total: data.total_storage});
        }
      })
      .catch((err) => console.error('Failed to fetch storage usage for PageShell:', err));
  }, []);

  /* ------------------------------------------------------------------
   * Storage gauge calculations
   * SVG circle: r=16 → circumference ≈ 100.53
   * ------------------------------------------------------------------ */
  const usedVal = dynamicStorage.used;
  const totalVal = dynamicStorage.total;
  const storagePercent = Math.round((usedVal / totalVal) * 100) || 0;
  const circumference = 2 * Math.PI * 16;
  const storageOffset = circumference - (storagePercent / 100) * circumference;

  /* ------------------------------------------------------------------
   * Popup / dropdown open state
   * ------------------------------------------------------------------ */
  const [storageOpen, setStorageOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const storageRef = useRef(null);
  const mobileMenuRef = useRef(null);

  /* Close popups on outside click */
  useEffect(() => {
    const handleClick = (e) => {
      if (storageRef.current && !storageRef.current.contains(e.target)) {
        setStorageOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* Get user display initial for avatar fallback */
  const userInitial = auth.user?.username ? auth.user.username.charAt(0).toUpperCase() : 'A';

  /* ------------------------------------------------------------------
   * Notification bell SVG (shared between desktop and mobile)
   * ------------------------------------------------------------------ */
  const BellIcon = ({size = 24}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3a6 6 0 00-6 6c0 3.1-.7 5-1.4 6.1-.4.6 0 1.4.8 1.4h13.2c.8 0 1.2-.8.8-1.4C18.7 14 18 12.1 18 9a6 6 0 00-6-6z"
        stroke="#1A1A2E"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path d="M10 19.5a2 2 0 004 0" stroke="#1A1A2E" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );

  /* ------------------------------------------------------------------
   * Storage gauge SVG widget (circular ring)
   * ------------------------------------------------------------------ */
  const StorageGauge = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
      {/* Background ring */}
      <circle
        cx="20"
        cy="20"
        r="16"
        fill="rgba(255,255,255,0.7)"
        stroke="rgba(196, 59, 113, 0.16)"
        strokeWidth="3.5"
      />
      {/* Progress arc */}
      <circle
        cx="20"
        cy="20"
        r="16"
        fill="none"
        stroke="#C43B71"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={storageOffset}
        transform="rotate(-90 20 20)"
      />
      {/* Center text */}
      <text
        x="20"
        y="20"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="var(--font-manrope), Manrope, system-ui"
        fontSize="11"
        fontWeight="800"
        fill="#1A1A2E"
      >
        {storagePercent}%
      </text>
    </svg>
  );

  /* ------------------------------------------------------------------
   * Storage info content block (shared between desktop popup and mobile menu)
   * ------------------------------------------------------------------ */
  const formatBytes = (bytes, decimals = 1) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const StorageInfo = () => (
    <div className="ps-storage-info">
      <p className="ps-storage-text">
        You have used <strong>{formatBytes(dynamicStorage.used)}</strong> of your{' '}
        <strong>{formatBytes(dynamicStorage.total)}</strong> storage space.
      </p>
      <div className="ps-storage-bar" aria-hidden="true">
        <div className="ps-storage-bar-fill" style={{width: `${storagePercent}%`}} />
      </div>
      <p className="ps-storage-remaining">
        {formatBytes(dynamicStorage.total - dynamicStorage.used)} left. Photos, X-rays, 3D scans, and record
        attachments all count toward the same limit.
      </p>
    </div>
  );

  return (
    <div className="page-shell">
      <div className={padContent ? 'page-shell-content' : ''}>
        {/* ============================================
         * Page Header Bar
         * [Back Button] [Title + Icon]  [Header Controls]
         * ============================================ */}
        {showHeader && (
          <div className="page-shell-header">
            {/* Left: Back button — always navigates to Home */}
            {showBackButton && (
              <button className="page-shell-back" onClick={() => navigate('/')} aria-label="Back to Home">
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 5l-7 7 7 7"
                    stroke="#C43B71"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {/* Center: Page title with optional small icon */}
            <div className="page-shell-title-area">
              {titleIcon && (
                <img src={titleIcon} alt="" className="page-shell-title-icon" aria-hidden="true" />
              )}
              <h1 className="page-shell-title">{title}</h1>
            </div>

            {/* ============================================
             * RIGHT SIDE — Desktop / Tablet (≥641px)
             * Shows: Notification bell, Storage gauge, Avatar
             * ============================================ */}
            <div className="ps-hc-wide">
              {/* Notification bell with unread badge */}
              <button
                type="button"
                className="ps-icon-tap"
                aria-label={`Notifications, ${unreadCount} unread`}
                onClick={() => navigate('/appointments')}
              >
                <BellIcon />
                {unreadCount > 0 && (
                  <span className="page-shell-badge" aria-hidden="true">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Storage gauge with popup */}
              <div className="ps-storage-wrap" ref={storageRef}>
                <button
                  type="button"
                  className="ps-icon-tap"
                  aria-label={`Storage, ${storagePercent}% used`}
                  aria-expanded={storageOpen}
                  aria-controls={storagePopId}
                  onClick={() => setStorageOpen(!storageOpen)}
                >
                  <StorageGauge />
                </button>
                {storageOpen && (
                  <div id={storagePopId} className="ps-storage-pop">
                    <StorageInfo />
                  </div>
                )}
              </div>

              {/* User avatar — navigates to Account/Profile */}
              <Link to="/account" className="ps-icon-tap ps-avatar-link" aria-label="Your profile">
                <div className="page-shell-avatar">
                  {auth.user?.avatar_url ? (
                    <img src={auth.user.avatar_url} alt={auth.user?.username || 'User'} />
                  ) : (
                    <span className="ps-avatar-initial">{userInitial}</span>
                  )}
                </div>
              </Link>
            </div>

            {/* ============================================
             * RIGHT SIDE — Mobile (≤640px)
             * Consolidated: single avatar button → dropdown menu
             * Contains: notifications, storage, profile link
             * ============================================ */}
            <div className="ps-hc-narrow" ref={mobileMenuRef}>
              <button
                type="button"
                className="ps-icon-tap ps-mobile-trigger"
                aria-expanded={mobileMenuOpen}
                aria-controls={mobileMenuId}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {/* Name comes from the visually-hidden text below; the avatar initial and the
                    badge are decorative duplicates of it, so they are hidden from AT. */}
                <span className="visually-hidden">{`Account menu, ${unreadCount} unread notifications`}</span>
                <div className="page-shell-avatar" aria-hidden="true">
                  {auth.user?.avatar_url ? (
                    <img src={auth.user.avatar_url} alt={auth.user?.username || 'User'} />
                  ) : (
                    <span className="ps-avatar-initial">{userInitial}</span>
                  )}
                </div>
                {/* Badge overlay on mobile avatar */}
                {unreadCount > 0 && (
                  <span className="ps-mobile-badge" aria-hidden="true">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Mobile dropdown menu */}
              {mobileMenuOpen && (
                <div id={mobileMenuId} className="ps-mobile-menu">
                  {/* Notifications row */}
                  <div
                    className="ps-mobile-row ps-mobile-row-clickable"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/appointments');
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setMobileMenuOpen(false);
                        navigate('/appointments');
                      }
                    }}
                  >
                    <BellIcon size={20} />
                    <span className="ps-mobile-label">Notifications</span>
                    {unreadCount > 0 && <span className="ps-mobile-count">{unreadCount} unread</span>}
                  </div>

                  {/* Storage section */}
                  <div className="ps-mobile-divider" />
                  <StorageInfo />

                  {/* Profile link */}
                  <div className="ps-mobile-divider" />
                  <Link
                    to="/account"
                    className="ps-mobile-row ps-mobile-row-clickable ps-mobile-profile"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="ps-mobile-avatar-sm">
                      {auth.user?.avatar_url ? (
                        <img src={auth.user.avatar_url} alt="" />
                      ) : (
                        <span>{userInitial}</span>
                      )}
                    </div>
                    <span className="ps-mobile-label">Your profile</span>
                    <span className="ps-mobile-chevron" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M9 5l7 7-7 7"
                          stroke="#C43B71"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================
         * Page Content Area
         * Children are rendered here inside the shell
         * ============================================ */}
        {children}
      </div>
    </div>
  );
}
